import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import CreditStatusBadge from './CreditStatusBadge';
import { formatCurrency } from '../../../utils/currency';

const CustomerProfileModal = ({ isOpen, onClose, customer }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !customer) return null;

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCreditUtilization = () => {
    if (customer?.creditLimit === 0) return 0;
    return ((customer?.outstandingBalance / customer?.creditLimit) * 100)?.toFixed(1);
  };

  const mockTransactions = [
    { id: 1, type: 'Invoice', number: 'INV-2026-001', date: '2026-01-28', amount: 5250.00, status: 'Paid' },
    { id: 2, type: 'Payment', number: 'PMT-2026-045', date: '2026-01-25', amount: -5250.00, status: 'Completed' },
    { id: 3, type: 'Invoice', number: 'INV-2026-002', date: '2026-01-20', amount: 8750.00, status: 'Outstanding' },
    { id: 4, type: 'Credit', number: 'CR-2026-003', date: '2026-01-15', amount: -500.00, status: 'Applied' },
    { id: 5, type: 'Invoice', number: 'INV-2025-089', date: '2025-12-28', amount: 6200.00, status: 'Paid' }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'contact', label: 'Contact Details', icon: 'User' },
    { id: 'credit', label: 'Credit Settings', icon: 'CreditCard' },
    { id: 'transactions', label: 'Transaction History', icon: 'Receipt' }
  ];

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl border border-border w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Building2" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">
                {customer?.customerName || customer?.companyName}
              </h2>
              <p className="font-body text-sm text-muted-foreground">
                Customer since {formatDate(customer?.since)}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            aria-label="Close modal"
          />
        </div>

        <div className="border-b border-border">
          <div className="flex overflow-x-auto">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 md:px-6 py-3 font-body text-sm font-medium whitespace-nowrap transition-smooth ${
                  activeTab === tab?.id
                    ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-muted/30 rounded-lg p-4">
                  <p className="font-body text-xs text-muted-foreground mb-1">Outstanding Balance</p>
                  <p className={`font-heading font-bold text-2xl ${
                    customer?.outstandingBalance > 0 ? 'text-warning' : 'text-success'
                  }`}>
                    {formatCurrency(customer?.outstandingBalance)}
                  </p>
                </div>
                <div className="bg-muted/30 rounded-lg p-4">
                  <p className="font-body text-xs text-muted-foreground mb-1">Credit Limit</p>
                  <p className="font-heading font-bold text-2xl text-foreground">
                    {formatCurrency(customer?.creditLimit)}
                  </p>
                  <p className="font-body text-xs text-muted-foreground mt-1">
                    {getCreditUtilization()}% utilized
                  </p>
                </div>
                <div className="bg-muted/30 rounded-lg p-4">
                  <p className="font-body text-xs text-muted-foreground mb-1">Total Paid</p>
                  <p className="font-heading font-bold text-2xl text-success">
                    {formatCurrency(customer?.totalPaid)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-heading font-semibold text-base text-foreground mb-3">Quick Stats</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="font-body text-sm text-muted-foreground">Total Invoices</span>
                      <span className="font-body font-medium text-sm text-foreground">{customer?.totalInvoices}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="font-body text-sm text-muted-foreground">Credit Status</span>
                      <CreditStatusBadge status={customer?.creditStatus} />
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="font-body text-sm text-muted-foreground">Payment Terms</span>
                      <span className="font-body font-medium text-sm text-foreground">{customer?.paymentTerms}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="font-body text-sm text-muted-foreground">Last Transaction</span>
                      <span className="font-body font-medium text-sm text-foreground">{formatDate(customer?.lastTransaction)}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-heading font-semibold text-base text-foreground mb-3">Contact Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Icon name="User" size={16} className="text-muted-foreground mt-0.5" />
                      <div>
                        <p className="font-body text-xs text-muted-foreground">Contact Person</p>
                        <p className="font-body text-sm text-foreground">{customer?.contactPerson}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Icon name="Mail" size={16} className="text-muted-foreground mt-0.5" />
                      <div>
                        <p className="font-body text-xs text-muted-foreground">Email</p>
                        <p className="font-body text-sm text-foreground">{customer?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Icon name="Phone" size={16} className="text-muted-foreground mt-0.5" />
                      <div>
                        <p className="font-body text-xs text-muted-foreground">Phone</p>
                        <p className="font-body text-sm text-foreground">{customer?.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Icon name="MapPin" size={16} className="text-muted-foreground mt-0.5" />
                      <div>
                        <p className="font-body text-xs text-muted-foreground">Address</p>
                        <p className="font-body text-sm text-foreground">{customer?.address}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="space-y-6">
              <div>
                <h3 className="font-heading font-semibold text-base text-foreground mb-4">Primary Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="font-body text-xs text-muted-foreground">Full Name</label>
                    <p className="font-body text-sm text-foreground">{customer?.contactPerson}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="font-body text-xs text-muted-foreground">Email Address</label>
                    <p className="font-body text-sm text-foreground">{customer?.email}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="font-body text-xs text-muted-foreground">Phone Number</label>
                    <p className="font-body text-sm text-foreground">{customer?.phone}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="font-body text-xs text-muted-foreground">Category</label>
                    <p className="font-body text-sm text-foreground">{customer?.category}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-base text-foreground mb-4">Business Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="font-body text-xs text-muted-foreground">Company Name</label>
                    <p className="font-body text-sm text-foreground">{customer?.customerName || customer?.companyName}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="font-body text-xs text-muted-foreground">Tax ID</label>
                    <p className="font-body text-sm text-foreground">{customer?.taxId}</p>
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <label className="font-body text-xs text-muted-foreground">Business Address</label>
                    <p className="font-body text-sm text-foreground">{customer?.address}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'credit' && (
            <div className="space-y-6">
              <div className="bg-muted/30 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-semibold text-base text-foreground">Credit Overview</h3>
                  <CreditStatusBadge status={customer?.creditStatus} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="font-body text-xs text-muted-foreground mb-1">Credit Limit</p>
                    <p className="font-heading font-bold text-xl text-foreground">
                      {formatCurrency(customer?.creditLimit)}
                    </p>
                  </div>
                  <div>
                    <p className="font-body text-xs text-muted-foreground mb-1">Available Credit</p>
                    <p className="font-heading font-bold text-xl text-success">
                      {formatCurrency(customer?.creditLimit - customer?.outstandingBalance)}
                    </p>
                  </div>
                  <div>
                    <p className="font-body text-xs text-muted-foreground mb-1">Utilization</p>
                    <p className="font-heading font-bold text-xl text-foreground">
                      {getCreditUtilization()}%
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-base text-foreground mb-4">Payment Terms</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="font-body text-xs text-muted-foreground">Standard Terms</label>
                    <p className="font-body text-sm text-foreground">{customer?.paymentTerms}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="font-body text-xs text-muted-foreground">Outstanding Balance</label>
                    <p className={`font-body font-semibold text-sm ${
                      customer?.outstandingBalance > 0 ? 'text-warning' : 'text-success'
                    }`}>
                      {formatCurrency(customer?.outstandingBalance)}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-base text-foreground mb-4">Credit History</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="font-body text-sm text-muted-foreground">Total Invoices Issued</span>
                    <span className="font-body font-medium text-sm text-foreground">{customer?.totalInvoices}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="font-body text-sm text-muted-foreground">Total Amount Paid</span>
                    <span className="font-body font-medium text-sm text-success">{formatCurrency(customer?.totalPaid)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="font-body text-sm text-muted-foreground">Customer Since</span>
                    <span className="font-body font-medium text-sm text-foreground">{formatDate(customer?.since)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'transactions' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-base text-foreground">Recent Transactions</h3>
                <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
                  Export
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left font-heading font-semibold text-sm text-foreground">Date</th>
                      <th className="px-4 py-3 text-left font-heading font-semibold text-sm text-foreground">Type</th>
                      <th className="px-4 py-3 text-left font-heading font-semibold text-sm text-foreground">Number</th>
                      <th className="px-4 py-3 text-right font-heading font-semibold text-sm text-foreground">Amount</th>
                      <th className="px-4 py-3 text-left font-heading font-semibold text-sm text-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {mockTransactions?.map(transaction => (
                      <tr key={transaction?.id} className="hover:bg-muted/30 transition-smooth">
                        <td className="px-4 py-3 font-body text-sm text-foreground">
                          {formatDate(transaction?.date)}
                        </td>
                        <td className="px-4 py-3 font-body text-sm text-foreground">
                          {transaction?.type}
                        </td>
                        <td className="px-4 py-3 font-body text-sm text-primary">
                          {transaction?.number}
                        </td>
                        <td className={`px-4 py-3 font-body font-medium text-sm text-right ${
                          transaction?.amount < 0 ? 'text-success' : 'text-foreground'
                        }`}>
                          {formatCurrency(Math.abs(transaction?.amount))}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            transaction?.status === 'Paid' || transaction?.status === 'Completed' || transaction?.status === 'Applied'
                              ? 'bg-success/10 text-success border border-success/20' :'bg-warning/10 text-warning border border-warning/20'
                          }`}>
                            {transaction?.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button variant="default" iconName="Edit" iconPosition="left">
            Edit Customer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfileModal;